import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  clearPortalTokens,
  getPortalTokens,
  isPortalAccessExpired,
} from "../utils/merchant-portal-storage";
import { refreshPortalTokens } from "../services/merchant-portal";

const PortalAuthContext = createContext(null);

export function PortalAuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  const syncAuthState = useCallback(() => {
    const { access_token, refresh_token, expires_at } = getPortalTokens();
    if (access_token && !isPortalAccessExpired(expires_at)) {
      setAuthenticated(true);
      return;
    }
    if (refresh_token) {
      setAuthenticated(true);
      return;
    }
    setAuthenticated(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { access_token, refresh_token, expires_at } = getPortalTokens();
      if (
        refresh_token &&
        (!access_token || isPortalAccessExpired(expires_at))
      ) {
        await refreshPortalTokens();
      }
      if (!cancelled) {
        syncAuthState();
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [syncAuthState]);

  const markLoggedIn = useCallback(() => {
    syncAuthState();
    setAuthenticated(true);
  }, [syncAuthState]);

  const markLoggedOut = useCallback(() => {
    clearPortalTokens();
    setAuthenticated(false);
  }, []);

  const value = useMemo(
    () => ({
      ready,
      authenticated,
      markLoggedIn,
      markLoggedOut,
    }),
    [ready, authenticated, markLoggedIn, markLoggedOut],
  );

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) {
    throw new Error("usePortalAuth must be used within PortalAuthProvider");
  }
  return ctx;
}
